import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;        // 15 minutes
const REFRESH_INTERVAL_MS = 4 * 60 * 1000; // refresh every 4 min

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

export const useIdleTimer = (onIdle: () => void) => {
  // Use refs to avoid stale closures and prevent useEffect re-runs
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const isIdle = useRef(false);
  const onIdleRef = useRef(onIdle);

  // Keep onIdleRef current without causing re-renders
  useEffect(() => {
    onIdleRef.current = onIdle;
  });

  useEffect(() => {
    const loginToken = localStorage.getItem('loginToken');
    if (!loginToken) return;

    const doRefreshToken = async () => {
      const token = localStorage.getItem('refreshToken');
      if (!token || isIdle.current) return;
      try {
        const res = await fetch(`${API_URL}/auth/refreshToken`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: token }),
        });
        const data = await res.json();
        if (data?.loginToken) {
          localStorage.setItem('loginToken', data.loginToken);
          if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
          console.log('[IdleTimer] Token refreshed successfully');
        }
      } catch (err) {
        console.error('[IdleTimer] Token refresh failed:', err);
      }
    };

    const resetIdleTimer = () => {
      if (isIdle.current) return;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        isIdle.current = true;
        if (refreshTimer.current) clearInterval(refreshTimer.current);
        console.log('[IdleTimer] User is idle — showing modal');
        onIdleRef.current();
      }, IDLE_TIMEOUT_MS);
    };

    // Start timers
    resetIdleTimer();
    refreshTimer.current = setInterval(doRefreshToken, REFRESH_INTERVAL_MS);

    // Attach listeners
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, resetIdleTimer));

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (refreshTimer.current) clearInterval(refreshTimer.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, []); // empty deps — runs once on mount only
};
